import { useState, useCallback, useRef } from 'react';

const MIN_TEXT_LENGTH = 500;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 Mo

// Détection type contenu (repris de PasteAsFileWidget)
function detectContentType(content) {
  if (!content || !content.trim()) return { type: 'text', ext: 'txt' };

  if (content.includes('function') || content.includes('=>') || content.includes('const ') || content.includes('import ')) {
    return { type: 'javascript', ext: 'js' };
  }
  if (content.includes('<html>') || content.includes('<!DOCTYPE') || content.includes('<div')) {
    return { type: 'html', ext: 'html' };
  }
  if (content.includes('# ') || content.includes('## ') || content.includes('```')) {
    return { type: 'markdown', ext: 'md' };
  }
  if (content.toUpperCase().includes('SELECT') || content.toUpperCase().includes('INSERT') || content.toUpperCase().includes('CREATE TABLE')) {
    return { type: 'sql', ext: 'sql' };
  }
  const csvLines = content.split('\n').filter(l => l.trim() && l.includes(','));
  if (csvLines.length >= 3) {
    const counts = csvLines.slice(0, 5).map(l => l.split(',').length);
    if (counts.every(c => c === counts[0] && c >= 3)) {
      return { type: 'csv', ext: 'csv' };
    }
  }
  return { type: 'text', ext: 'txt' };
}

function generateFilename(content, detected, isImage) {
  const now = new Date();
  const ts = now.toISOString().slice(0, 16).replace('T', '_').replace(':', 'h');

  if (isImage) return `screenshot_${ts}.png`;

  let name = 'paste';
  if (detected.type === 'javascript') {
    const match = content.match(/(?:function|class)\s+(\w+)/);
    name = match ? match[1] : 'script';
  } else if (detected.type === 'markdown') {
    const header = content.match(/^#\s+(.+)/m);
    name = header ? header[1].toLowerCase().replace(/\s+/g, '_').slice(0, 30) : 'document';
  } else if (detected.type === 'sql') {
    const match = content.match(/CREATE TABLE\s+(\w+)/i);
    name = match ? match[1] : 'query';
  } else if (detected.type === 'html') {
    name = 'page';
  } else if (detected.type === 'csv') {
    name = 'data';
  }

  return `paste_${name}_${ts}.${detected.ext}`;
}

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' o';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
  return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
}

const MAX_DIMENSION = 1920; // Redimensionner si > 1920px
const JPEG_QUALITY = 0.85;

function compressImage(blob) {
  return new Promise((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(blob);

    const fallback = () => {
      URL.revokeObjectURL(url);
      const reader = new FileReader();
      reader.onload = () => resolve({ base64: reader.result, size: blob.size, ext: 'png' });
      reader.readAsDataURL(blob);
    };

    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const needsResize = width > MAX_DIMENSION || height > MAX_DIMENSION;

      if (!needsResize && blob.size <= MAX_IMAGE_SIZE) {
        // Pas besoin de compresser — garder l'original
        const reader = new FileReader();
        reader.onload = () => resolve({ base64: reader.result, size: blob.size, ext: 'png' });
        reader.readAsDataURL(blob);
        return;
      }

      if (needsResize) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);

      // Essayer PNG d'abord (meilleur pour screenshots UI)
      canvas.toBlob((pngBlob) => {
        if (!pngBlob) { fallback(); return; }

        // Essayer JPEG aussi et garder le plus petit
        canvas.toBlob((jpegBlob) => {
          const best = (jpegBlob && jpegBlob.size < pngBlob.size) ? jpegBlob : pngBlob;
          const bestExt = (best === jpegBlob) ? 'jpg' : 'png';
          const reader = new FileReader();
          reader.onload = () => resolve({ base64: reader.result, size: best.size, ext: bestExt });
          reader.readAsDataURL(best);
        }, 'image/jpeg', JPEG_QUALITY);
      }, 'image/png');
    };

    img.onerror = fallback;
    img.src = url;
  });
}

let nextId = 1;

export function usePasteHandler({ onError } = {}) {
  const [attachments, setAttachments] = useState([]);
  const attachmentsRef = useRef([]);

  const addAttachment = useCallback((att) => {
    const newAtt = { ...att, id: nextId++ };
    setAttachments(prev => {
      const next = [...prev, newAtt];
      attachmentsRef.current = next;
      return next;
    });
  }, []);

  const handlePaste = useCallback((e) => {
    if (!e.clipboardData) return;
    const items = e.clipboardData.items;

    // Vérifier si une image est dans le clipboard
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type && item.type.startsWith('image/')) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (!blob) return;

          compressImage(blob).then(({ base64, size, ext }) => {
            if (size > MAX_IMAGE_SIZE) {
              if (onError) onError(`Image trop lourde apres compression (${formatSize(size)}). Maximum : ${formatSize(MAX_IMAGE_SIZE)}`);
              return;
            }
            addAttachment({
              name: generateFilename(null, {}, true).replace('.png', '.' + ext),
              size,
              sizeLabel: formatSize(size),
              type: 'image',
              ext,
              content: null,
              base64,
              blob: null,
              blobUrl: base64, // Utiliser base64 directement comme src thumbnail
              isImage: true,
              destination: 'discussion',
            });
          });
          return;
        }
      }
    }

    // Vérifier texte long
    const text = e.clipboardData.getData('text/plain') || e.clipboardData.getData('text');
    if (text && text.length > MIN_TEXT_LENGTH) {
      e.preventDefault();
      const detected = detectContentType(text);
      const filename = generateFilename(text, detected, false);
      const textSize = new TextEncoder().encode(text).length;
      addAttachment({
        name: filename,
        size: textSize,
        sizeLabel: formatSize(textSize),
        type: detected.type,
        ext: detected.ext,
        content: text,
        base64: null,
        blob: null,
        blobUrl: null,
        isImage: false,
        destination: 'discussion',
      });
    }
    // Texte court : détecter les URLs
    const urlMatch = (text || '').match(/^https?:\/\/[^\s<>"']+$/i);
    if (urlMatch && text.length <= MIN_TEXT_LENGTH) {
      e.preventDefault();
      addAttachment({
        name: urlMatch[0],
        size: 0,
        sizeLabel: '',
        type: 'url',
        ext: 'url',
        content: urlMatch[0],
        base64: null,
        blob: null,
        blobUrl: null,
        isImage: false,
        isUrl: true,
        destination: 'discussion',
        ogData: null,
      });
      return;
    }
    // Texte court : laisser le comportement par défaut (pas de preventDefault)
  }, [addAttachment, onError]);

  const removeAttachment = useCallback((id) => {
    setAttachments(prev => {
      const att = prev.find(a => a.id === id);
      if (att?.blobUrl) URL.revokeObjectURL(att.blobUrl);
      const next = prev.filter(a => a.id !== id);
      attachmentsRef.current = next;
      return next;
    });
  }, []);

  const updateDestination = useCallback((id, destination) => {
    setAttachments(prev => {
      const next = prev.map(a => a.id === id ? { ...a, destination } : a);
      attachmentsRef.current = next;
      return next;
    });
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments(prev => {
      prev.forEach(a => { if (a.blobUrl) URL.revokeObjectURL(a.blobUrl); });
      attachmentsRef.current = [];
      return [];
    });
  }, []);

  // Ajouter une URL pour analyse (Session 25 — fetch_url)
  const addUrl = useCallback((urlString) => {
    if (!urlString || !urlString.match(/^https?:\/\//i)) return;
    // Éviter les doublons
    if (attachmentsRef.current.some(a => a.isUrl && a.content === urlString)) return;
    addAttachment({
      name: urlString,
      size: 0,
      sizeLabel: '',
      type: 'url',
      ext: 'url',
      content: urlString,
      base64: null,
      blob: null,
      blobUrl: null,
      isImage: false,
      isUrl: true,
      destination: 'discussion',
      ogData: null,
    });
  }, [addAttachment]);

  // Ajouter des fichiers depuis un <input type="file"> (Session 17 — Menu "+")
  const addFiles = useCallback((fileList) => {
    Array.from(fileList).forEach(file => {
      const isImage = file.type && file.type.startsWith('image/');
      if (isImage) {
        compressImage(file).then(({ base64, size, ext }) => {
          if (size > MAX_IMAGE_SIZE) {
            if (onError) onError(`Image trop lourde (${formatSize(size)}). Maximum : ${formatSize(MAX_IMAGE_SIZE)}`);
            return;
          }
          addAttachment({
            name: file.name || generateFilename(null, {}, true).replace('.png', '.' + ext),
            size,
            sizeLabel: formatSize(size),
            type: 'image',
            ext,
            content: null,
            base64,
            blob: null,
            blobUrl: base64,
            isImage: true,
            destination: 'discussion',
          });
        });
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          const text = reader.result;
          const detected = detectContentType(text);
          const fileExt = file.name ? file.name.split('.').pop() : detected.ext;
          addAttachment({
            name: file.name || generateFilename(text, detected, false),
            size: file.size,
            sizeLabel: formatSize(file.size),
            type: detected.type,
            ext: fileExt,
            content: text,
            base64: null,
            blob: null,
            blobUrl: null,
            isImage: false,
            destination: 'discussion',
          });
        };
        reader.readAsText(file);
      }
    });
  }, [addAttachment, onError]);

  return {
    attachments,
    handlePaste,
    addFiles,
    addUrl,
    removeAttachment,
    updateDestination,
    clearAttachments,
  };
}
